import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, Info, AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { SUPABASE_URL } from "@/constants/supabase";
import { useToast } from "@/components/ui/use-toast";

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
  documentRequests: z.boolean().default(true),
  informationRequests: z.boolean().default(true),
  serviceUpdates: z.boolean().default(true),
  marketingCommunications: z.boolean().default(false),
  unsubscribeAll: z.boolean().default(false),
});

export default function EmailPreference() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get("email") || "";
  const tokenParam = searchParams.get("token") || "";
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: emailParam,
      documentRequests: true,
      informationRequests: true,
      serviceUpdates: true,
      marketingCommunications: false,
      unsubscribeAll: false,
    },
  });

  // Verify token and load preferences when component mounts
  useEffect(() => {
    if (emailParam && tokenParam) {
      verifyTokenAndLoadPreferences(emailParam, tokenParam);
    }
  }, [emailParam, tokenParam]);

  // Function to verify token and load preferences
  async function verifyTokenAndLoadPreferences(email: string, token: string) {
    setIsLoading(true);
    setError(null);

    try {
      // Get preferences for the email
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/get-preferences`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to load preferences");
      }

      // Update form with preferences
      form.setValue("documentRequests", data.preferences.documentRequests);
      form.setValue(
        "informationRequests",
        data.preferences.informationRequests
      );
      form.setValue("serviceUpdates", data.preferences.serviceUpdates);
      form.setValue(
        "marketingCommunications",
        data.preferences.marketingCommunications
      );
      form.setValue("unsubscribeAll", data.preferences.unsubscribedAll);
    } catch (err) {
      console.error("Error loading preferences:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load preferences"
      );
    } finally {
      setIsLoading(false);
    }
  }

  // Function to handle form submission
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare the request body
      const requestBody = {
        email: values.email,
        token: tokenParam, // Include token if available
        preferences: {
          documentRequests: values.documentRequests,
          informationRequests: values.informationRequests,
          serviceUpdates: values.serviceUpdates,
          marketingCommunications: values.marketingCommunications,
          unsubscribeAll: values.unsubscribeAll,
        },
      };

      // Update preferences
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/email-preferences/update-preferences`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabase.auth.getSession()}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update preferences");
      }

      setIsSubmitted(true);
      toast({
        title: "Preferences Updated",
        description: "Your email preferences have been updated successfully.",
      });
    } catch (err) {
      console.error("Error updating preferences:", err);
      setError(
        err instanceof Error ? err.message : "Failed to update preferences"
      );
      toast({
        variant: "destructive",
        title: "Error",
        description:
          err instanceof Error
            ? err.message
            : "Failed to update preferences. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="container max-w-3xl py-10">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-2">Email Preference Center</h1>
        <p className="text-muted-foreground">
          Manage your communication preferences with Numberz
        </p>
      </div>

      {isLoading && !error && !isSubmitted ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-center text-muted-foreground">
              {tokenParam
                ? "Verifying your email and loading preferences..."
                : "Loading..."}
            </p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Error
            </CardTitle>
            <CardDescription>
              We encountered a problem processing your request.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  window.location.href = "/";
                }}
              >
                Return to Homepage
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : isSubmitted ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Preferences Updated
            </CardTitle>
            <CardDescription>
              Your email preferences have been updated successfully.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              You will now receive emails based on your selected preferences.
              You can update these preferences at any time.
            </p>
            <Button onClick={() => setIsSubmitted(false)}>
              Update Preferences Again
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Alert className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertTitle className="text-blue-800 dark:text-blue-300">
                Email Preference Center
              </AlertTitle>
              <AlertDescription className="text-blue-700 dark:text-blue-400">
                Select which types of emails you'd like to receive from Numberz.
                You can change these preferences at any time.
              </AlertDescription>
            </Alert>

            <Card>
              <CardHeader>
                <CardTitle>Your Email Preferences</CardTitle>
                <CardDescription>
                  Customize the types of emails you receive
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <input
                            type="email"
                            className="w-full p-2 border rounded-md bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                            placeholder="your@email.com"
                            {...field}
                            disabled={!!emailParam}
                          />
                        </div>
                      </FormControl>
                      <FormDescription>
                        This is the email address for your preferences
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-medium">Email Types</h3>

                  <FormField
                    control={form.control}
                    name="documentRequests"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Document Requests
                          </FormLabel>
                          <FormDescription>
                            Notifications when we need documents from you
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={form.watch("unsubscribeAll")}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="informationRequests"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Information Requests
                          </FormLabel>
                          <FormDescription>
                            Requests for additional information needed for your
                            tax preparation
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={form.watch("unsubscribeAll")}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="serviceUpdates"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Service Updates
                          </FormLabel>
                          <FormDescription>
                            Important updates about our services and tax law
                            changes
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={form.watch("unsubscribeAll")}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="marketingCommunications"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">
                            Marketing Communications
                          </FormLabel>
                          <FormDescription>
                            Newsletters, tax tips, and promotional offers
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={form.watch("unsubscribeAll")}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="pt-6 border-t mt-6">
                  <FormField
                    control={form.control}
                    name="unsubscribeAll"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 bg-gray-50 dark:bg-gray-800">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base font-medium">
                            Unsubscribe from all emails
                          </FormLabel>
                          <FormDescription>
                            You will no longer receive any emails from Numberz
                            except for essential account notifications.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={(checked) => {
                              field.onChange(checked);
                              if (checked) {
                                form.setValue("documentRequests", false);
                                form.setValue("informationRequests", false);
                                form.setValue("serviceUpdates", false);
                                form.setValue("marketingCommunications", false);
                              }
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => (window.location.href = "/")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Preferences"
                )}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  );
}
